/**
 * Commodity Administration Page (KAN-259)
 *
 * Tabbed admin page for the Commodities module.
 * Currently supports:
 *   - Import Configs: manage CSV/Excel import configurations
 *
 * Pattern mirrors AdminDashboard.tsx (FX admin) but scoped to Commodities.
 */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SystemDateDisplay from '../../../components/common/SystemDateDisplay';
import TradeImportConfigPanel from '../../../components/settings/TradeImportConfigPanel';
import './CommodityAdmin.css';

const validTabs = ['subscriptionImportConfigs', 'expiryImportConfigs'] as const;
type CommodityAdminTab = typeof validTabs[number];

const CommodityAdmin: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab') as CommodityAdminTab;
  const initialTab: CommodityAdminTab =
    validTabs.includes(requestedTab) ? requestedTab : 'subscriptionImportConfigs';
  const [activeTab, setActiveTab] = useState<CommodityAdminTab>(initialTab);

  return (
    <div className="commodity-admin-page">
      <div className="commodity-admin-header">
        <h2 className="commodity-admin-title">{t('commodities.admin.title')}</h2>
        <SystemDateDisplay />
      </div>

      <div className="commodity-admin-tabs">
        {validTabs.map((tab) => (
          <button
            key={tab}
            className={`tab-button ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {t(`commodities.admin.tabs.${tab}`)}
          </button>
        ))}
      </div>

      <div className="commodity-admin-content">
        {activeTab === 'subscriptionImportConfigs' && (
          <TradeImportConfigPanel module="Commodities" importContext="Subscription" />
        )}
        {activeTab === 'expiryImportConfigs' && (
          <TradeImportConfigPanel module="Commodities" importContext="Expiry" />
        )}
      </div>
    </div>
  );
};

export default CommodityAdmin;
